import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { API } from "@services/http.service";
import showErrMessage from "@components/ShowErrMessage";
import showMessage from "@components/ShowMessage";
import LegalText from "@components/LegalText";
import { LEGAL_TEXT_TYPES } from "../../constants/legalText.const";

import "./styles.scss";

/**
 * Açık Rıza Ticari İleti İzni modalı (İYS S7 — ilk-giriş onayı).
 *
 * Amaç: admin tarafından İYS onayı ALINMADAN kaydedilmiş (SMS izni kapalı) kullanıcılardan,
 * portala ilk girişlerinde ticari elektronik ileti izni almak.
 *
 * Akış:
 *  - Modal, disclaimer akışından SONRA ve hoşgeldin popup'ından ÖNCE çıkar
 *    (disclaimer tamamlanmasını bekler; çözülünce onResolved ile sıradaki modala devreder).
 *  - Gösterim kararı SUNUCU tarafında verilir: GET /employee/commercialConsent/status
 *    (SMS izni kapalı && kullanıcı henüz karar vermemiş → true).
 *  - "İzin Veriyorum" → approved=true: SMS izni açılır + İYS'ye MESAJ/ONAY (HS_WEB) bildirilir.
 *  - "İzin Vermiyorum" → approved=false: SMS izni kapalı kalır + İYS'ye MESAJ/RET bildirilir.
 *  - Her iki durumda da sunucu "karar verildi" bayrağını set eder → modal bir daha çıkmaz.
 *  - Karar verilmeden kapatılamaz (disclaimer gibi); böylece izin akışı tamamlanır.
 *
 * Sözleşme (WelcomePopup ile aynı, sıra orchestrator'da belirlenir):
 *  - enabled: true olana kadar bekler (önceki modal çözülene dek).
 *  - onResolved: gösterilmeyecekse ya da karar verilip kapandığında bir kez çağrılır.
 */
const CommercialConsentModal = ({ enabled = true, onResolved }) => {
  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  // Disclaimer akışından SONRA çıkmalı (welcome popup ile aynı önkoşul).
  const disclaimerInitialized = useSelector(
    (store) => store.disclaimer.initialized
  );
  const disclaimerRequired = useSelector((store) => store.disclaimer.required);

  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Aynı oturumda durum sorgusunun tekrar tekrar çalışmasını engeller.
  const checkedRef = useRef(false);
  // onResolved yalnızca bir kez tetiklensin.
  const resolvedRef = useRef(false);
  const resolve = () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    if (onResolved) onResolved();
  };

  useEffect(() => {
    if (!isAuthenticated || !enabled || checkedRef.current) return;
    // Disclaimer henüz çözülmediyse ya da hâlâ zorunluysa bekle.
    if (!disclaimerInitialized || disclaimerRequired !== false) return;
    checkedRef.current = true;

    let active = true;
    (async () => {
      try {
        const response = await API.get("/employee/commercialConsent/status");
        if (!active) return;
        // ResponseData<Boolean> → http.service response.data alanına açar
        if (response?.data === true) {
          setShow(true);
        } else {
          // Gösterilmeyecek → sıradaki modala (hoşgeldin popup) devret.
          resolve();
        }
      } catch (error) {
        // Durum alınamazsa modal gösterme; sessizce sıradakine devret.
        resolve();
      }
    })();

    return () => {
      active = false;
    };
  }, [isAuthenticated, enabled, disclaimerInitialized, disclaimerRequired]);

  const submitDecision = async (approved) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await API.post(
        `/employee/commercialConsent/decision?approved=${approved}`
      );
      showMessage(
        approved
          ? "İzniniz kaydedildi. Teşekkür ederiz."
          : "Tercihiniz kaydedildi."
      );
      setShow(false);
      // Karar verildi → sıradaki modal (hoşgeldin popup) devreye girsin.
      resolve();
    } catch (error) {
      showErrMessage(
        error?.message || "İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin."
      );
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="commercial-consent-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="commercialConsentTitle"
      onKeyDown={(e) => {
        // ESC ile kapanmayı engelle — karar verilmeli.
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div className="commercial-consent-modal">
        <div className="commercial-consent-header">
          <h4 id="commercialConsentTitle">Açık Rıza Ticari İleti İzni</h4>
        </div>

        <div className="commercial-consent-body">
          <LegalText
            type={LEGAL_TEXT_TYPES.TICARI_ELEKTRONIK_ILETI_IZNI}
            className=""
          />
        </div>

        <div className="commercial-consent-footer">
          <button
            type="button"
            className="btn btn-outline-danger px-4"
            onClick={() => submitDecision(false)}
            disabled={submitting}
          >
            İzin Vermiyorum
          </button>
          <button
            type="button"
            className="btn btn-blue px-4"
            onClick={() => submitDecision(true)}
            disabled={submitting}
          >
            {submitting && (
              <span className="spinner-border spinner-border-sm me-2" />
            )}
            İzin Veriyorum
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommercialConsentModal;
